import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";

import api from "../services/api";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { parseApiDateTime } from "../utils/datetime";
import OriginIcon from "../components/ui/OriginIcon";

const statusStyles = {
  pendente: "bg-amber-100 text-amber-700",
  comprado: "bg-green-100 text-green-700",
  cancelada: "bg-gray-100 text-gray-700",
};

const statusLabels = {
  pendente: "Pendente",
  comprado: "Comprado",
  cancelada: "Cancelada",
};

const formatNumber = (value) =>
  new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const formatDateTime = (value) => {
  const date = parseApiDateTime(value);
  if (!date) return "—";
  return date.toLocaleString("pt-BR", {
    timeZone: "America/Sao_Paulo",
    dateStyle: "short",
    timeStyle: "short",
  });
};

const StockPurchaseRequests = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pendente");
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await api.get("/stock/purchase-requests", {
        params: { status_filter: statusFilter || "todos" },
      });
      setRequests(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Erro ao carregar solicitações de compra:", error.response?.data || error);
      alert("Erro ao carregar solicitações de compra.");
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [statusFilter]);

  const pendingRequests = useMemo(
    () => requests.filter((request) => request.status === "pendente"),
    [requests]
  );

  const markPurchased = async (requestId) => {
    try {
      await api.post(`/stock/purchase-requests/${requestId}/mark-purchased`);
      await loadData();
    } catch (error) {
      console.error("Erro ao marcar compra:", error.response?.data || error);
      alert(error.response?.data?.detail || "Erro ao marcar compra.");
    }
  };

  const cancelRequest = async (requestId) => {
    try {
      await api.post(`/stock/purchase-requests/${requestId}/cancel`);
      await loadData();
    } catch (error) {
      console.error("Erro ao cancelar solicitação de compra:", error.response?.data || error);
      alert(error.response?.data?.detail || "Erro ao cancelar solicitação de compra.");
    }
  };

  const handlePrint = () => {
    const printableRows = pendingRequests
      .map(
        (request, index) => `
          <tr>
            <td>${index + 1}</td>
            <td>${request.item_name || "Item removido"}</td>
            <td>${request.item_sku || "Sem SKU"}</td>
            <td>${request.supplier_name || "Sem fornecedor"}</td>
            <td>${formatNumber(request.current_stock)}</td>
            <td>${formatNumber(request.reorder_point)}</td>
            <td>${formatNumber(request.ideal_stock)}</td>
            <td>${formatNumber(request.requested_quantity)}</td>
            <td>${request.reason || "Ponto de pedido"}</td>
          </tr>
        `
      )
      .join("");

    const printWindow = window.open("", "_blank", "width=1024,height=768");
    if (!printWindow) {
      alert("Não foi possível abrir a janela de impressão.");
      return;
    }

    printWindow.document.write(`
      <html>
        <head>
          <title>Solicitações de Compra Pendentes</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 24px; color: #111827; }
            h1 { margin-bottom: 8px; }
            p { color: #4b5563; margin-top: 0; }
            table { width: 100%; border-collapse: collapse; margin-top: 24px; }
            th, td { border: 1px solid #d1d5db; padding: 10px; text-align: left; font-size: 12px; }
            th { background: #f3f4f6; }
          </style>
        </head>
        <body>
          <h1>Solicitações de Compra Pendentes</h1>
          <p>Emitido em ${new Date().toLocaleString("pt-BR")}</p>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Item</th>
                <th>SKU</th>
                <th>Fornecedor</th>
                <th>Estoque Atual</th>
                <th>Ponto de Pedido</th>
                <th>Estoque Ideal</th>
                <th>Qtd Sugerida</th>
                <th>Motivo</th>
              </tr>
            </thead>
            <tbody>
              ${printableRows || '<tr><td colspan="9">Nenhuma solicitação pendente.</td></tr>'}
            </tbody>
          </table>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" aria-label="Voltar" onClick={() => navigate("/stock")}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Solicitações de Compra</h1>
              <p className="text-gray-500">
                Lista automática de itens que chegaram ao ponto de pedido.
              </p>
            </div>
          </div>

          <Button variant="outline" onClick={handlePrint}>
            <Printer className="mr-2 h-4 w-4" />
            Imprimir Pendências
          </Button>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-gray-500">Pendentes</p>
              <p className="mt-2 text-2xl font-semibold text-gray-900">
                {requests.filter((request) => request.status === "pendente").length}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-gray-500">Compradas</p>
              <p className="mt-2 text-2xl font-semibold text-gray-900">
                {requests.filter((request) => request.status === "comprado").length}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-gray-500">Canceladas</p>
              <p className="mt-2 text-2xl font-semibold text-gray-900">
                {requests.filter((request) => request.status === "cancelada").length}
              </p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Fila de Compras</h2>
                <p className="text-sm text-gray-500">
                  Analistas podem consultar e imprimir tudo que está pendente para comprar.
                </p>
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="pendente">Pendentes</option>
                <option value="">Todos os status</option>
                <option value="comprado">Compradas</option>
                <option value="cancelada">Canceladas</option>
              </select>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-8 text-center text-gray-500">Carregando solicitações de compra...</div>
            ) : requests.length === 0 ? (
              <div className="py-8 text-center text-gray-500">
                Nenhuma solicitação de compra encontrada.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[1100px]">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="px-2 py-3 text-center text-sm font-semibold text-gray-600" title="Origem: robô = automática, pessoa = manual">Origem</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Item</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Fornecedor</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Estoque</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Pedido / Ideal</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Qtd Sugerida</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Motivo</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Gerada em</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Status</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requests.map((request) => (
                      <tr key={request.id} className="border-b border-gray-100 align-top">
                        <td className="px-2 py-3">
                          <div className="flex justify-center pt-0.5">
                            <OriginIcon origin={request.generated_automatically ? "auto" : "manual"} size={26} />
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          <div className="font-medium">{request.item_name || "Item removido"}</div>
                          <div className="text-xs text-gray-500">{request.item_sku || "Sem SKU"}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {request.supplier_name || "Sem fornecedor"}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {formatNumber(request.current_stock)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          <div>Pedido: {formatNumber(request.reorder_point)}</div>
                          <div>Ideal: {formatNumber(request.ideal_stock)}</div>
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {formatNumber(request.requested_quantity)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          <div>{request.reason || "Ponto de pedido"}</div>
                          <div className="text-xs text-gray-500">{request.notes || "—"}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {formatDateTime(request.generated_at || request.created_at)}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span
                            className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                              statusStyles[request.status] || "bg-gray-100 text-gray-700"
                            }`}
                          >
                            {statusLabels[request.status] || request.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right text-sm">
                          <div className="flex justify-end gap-2">
                            {request.status === "pendente" && (
                              <>
                                <Button
                                  variant="outline"
                                  onClick={() => markPurchased(request.id)}
                                  className="h-auto px-3 py-1 text-xs border-green-200 text-green-700 hover:bg-green-50"
                                >
                                  Marcar comprada
                                </Button>
                                <Button
                                  variant="outline"
                                  onClick={() => cancelRequest(request.id)}
                                  className="h-auto px-3 py-1 text-xs text-gray-700"
                                >
                                  Cancelar
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default StockPurchaseRequests;
